import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Toggle,
  Badge,
  Tabs,
  ErrorBanner,
  formatDate,
} from '../components/ui';

interface Review {
  id: string;
  rating: number;
  body?: string;
  authorName?: string;
  source?: string;
  responseBody?: string;
  createdAt: string;
}

interface ReputationSettings {
  requestEnabled: boolean;
  requestDelayHours?: number;
  requestTemplate?: string;
  autoRespondEnabled?: boolean;
}

type Tab = 'reviews' | 'requests' | 'widget' | 'analytics';

export function ReputationPage() {
  const [tab, setTab] = useState<Tab>('reviews');
  return (
    <div>
      <PageHeader title="Reputation" subtitle="Reviews and ratings across channels" />
      <Tabs<Tab>
        active={tab}
        onChange={setTab}
        tabs={[
          { value: 'reviews', label: 'Reviews' },
          { value: 'requests', label: 'Request flow' },
          { value: 'widget', label: 'Widget' },
          { value: 'analytics', label: 'Analytics' },
        ]}
      />
      {tab === 'reviews' && <Reviews />}
      {tab === 'requests' && <Requests />}
      {tab === 'widget' && <WidgetPanel />}
      {tab === 'analytics' && <Analytics />}
    </div>
  );
}

function Reviews() {
  const [drafting, setDrafting] = useState<Review | null>(null);
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['reviews'],
    queryFn: () => api.get<{ data: Review[] }>('/reviews').catch(() => ({ data: [] })),
  });
  const reviews = q.data?.data ?? [];

  if (q.isLoading) return <Spinner />;
  if (reviews.length === 0) {
    return (
      <Card>
        <EmptyState title="No reviews yet" description="Reviews from Google, Facebook, and Yelp show up here." />
      </Card>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
      {reviews.map((r) => (
        <Card key={r.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600 }}>
                {'★'.repeat(r.rating)}
                {'☆'.repeat(5 - r.rating)}
              </div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                {r.authorName ?? 'Anonymous'} via {r.source ?? 'web'} · {formatDate(r.createdAt)}
              </div>
            </div>
            <Badge color={r.responseBody ? 'green' : 'yellow'}>{r.responseBody ? 'Responded' : 'Needs reply'}</Badge>
          </div>
          {r.body && <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)' }}>{r.body}</p>}
          {r.responseBody && (
            <div style={{ marginTop: 'var(--space-2)', padding: 'var(--space-2)', background: 'var(--color-surface-secondary)', borderRadius: 'var(--radius-md)' }}>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)', marginBottom: 4 }}>Your response</div>
              <div style={{ fontSize: 'var(--text-sm)' }}>{r.responseBody}</div>
            </div>
          )}
          {!r.responseBody && (
            <Button size="sm" variant="secondary" onClick={() => setDrafting(r)} style={{ marginTop: 'var(--space-2)' }}>
              Draft AI response
            </Button>
          )}
        </Card>
      ))}
      {drafting && (
        <ResponseModal
          review={drafting}
          onClose={() => setDrafting(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['reviews'] });
            setDrafting(null);
          }}
        />
      )}
    </div>
  );
}

function ResponseModal({
  review,
  onClose,
  onSaved,
}: {
  review: Review;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const draftMut = useMutation({
    mutationFn: () => api.post<{ draft: string }>(`/reviews/${review.id}/draft-response`),
    onSuccess: (r) => setDraft(r.draft),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Draft failed'),
  });

  const saveMut = useMutation({
    mutationFn: () => api.patch(`/reviews/${review.id}`, { responseBody: draft }),
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  return (
    <Modal onClose={onClose} title="Respond to review" width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ padding: 'var(--space-2)', background: 'var(--color-surface-secondary)', borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)' }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{'★'.repeat(review.rating)}</div>
          {review.body}
        </div>
        <Field label="Response">
          <Textarea value={draft} onChange={setDraft} rows={4} placeholder="Write or generate a response…" />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="secondary" onClick={() => draftMut.mutate()} disabled={draftMut.isPending}>
            {draftMut.isPending ? 'Drafting…' : 'AI draft'}
          </Button>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => saveMut.mutate()} disabled={!draft || saveMut.isPending}>
              {saveMut.isPending ? 'Posting…' : 'Post response'}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Requests() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['reputation-settings'],
    queryFn: () =>
      api.get<ReputationSettings>('/settings/reputation').catch(
        (): ReputationSettings => ({ requestEnabled: false })
      ),
  });
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [delayHours, setDelay] = useState<string>('');
  const [template, setTemplate] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const settings = q.data;
  const en = enabled ?? settings?.requestEnabled ?? false;
  const dh = delayHours || String(settings?.requestDelayHours ?? 24);
  const tpl = template || settings?.requestTemplate || 'Hi {{firstName}}, would you take a moment to leave a review?';

  const saveMut = useMutation({
    mutationFn: () =>
      api.patch('/settings/reputation', {
        requestEnabled: en,
        requestDelayHours: Number(dh) || 24,
        requestTemplate: tpl,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['reputation-settings'] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Save failed'),
  });

  if (q.isLoading) return <Spinner />;

  return (
    <Card>
      <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>
        Auto-request reviews after each booking
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <Toggle checked={en} onChange={setEnabled} label="Send review requests automatically" />
        <Field label="Delay (hours after booking ends)">
          <Input value={dh} onChange={setDelay} type="number" min={1} />
        </Field>
        <Field label="Message template" hint="Use {{firstName}}, {{businessName}}, {{reviewLink}} as variables">
          <Textarea value={tpl} onChange={setTemplate} rows={4} />
        </Field>
        <ErrorBanner error={error} />
        <div>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
            {saveMut.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function WidgetPanel() {
  const tenantSlug = (typeof window !== 'undefined' ? window.localStorage.getItem('frontdesk-tenant-slug') : null) ?? 'your-business';
  const embed = `<script src="${(import.meta.env.VITE_API_URL as string | undefined) ?? 'https://frontdesk-api.blancorunning.workers.dev'}/widget/reviews/${tenantSlug}.js" async></script>`;
  return (
    <Card>
      <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-base)', fontWeight: 600 }}>
        Public review widget
      </h3>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
        Drop this on your website to show your latest reviews. Updates automatically.
      </p>
      <pre
        style={{
          background: 'var(--color-surface-secondary)',
          padding: 'var(--space-3)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--text-xs)',
          overflowX: 'auto',
        }}
      >
        {embed}
      </pre>
    </Card>
  );
}

function Analytics() {
  const q = useQuery({
    queryKey: ['reviews-analytics'],
    queryFn: () =>
      api
        .get<{ averageRating: number; total: number; byRating: Record<string, number> }>('/reviews/analytics')
        .catch(() => ({ averageRating: 0, total: 0, byRating: {} })),
  });
  if (q.isLoading) return <Spinner />;
  const data = q.data!;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--space-3)' }}>
      <Card>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Average rating</div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>
          {data.averageRating.toFixed(1)} {'★'}
        </div>
      </Card>
      <Card>
        <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Total reviews</div>
        <div style={{ fontSize: 'var(--text-3xl)', fontWeight: 700 }}>{data.total}</div>
      </Card>
      <Card style={{ gridColumn: '1 / -1' }}>
        <h4 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>By rating</h4>
        {Object.entries(data.byRating).map(([rating, count]) => (
          <div key={rating} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
            <span style={{ width: 40, fontSize: 'var(--text-sm)' }}>{rating} ★</span>
            <div style={{ flex: 1, height: 8, background: 'var(--color-surface-secondary)', borderRadius: 'var(--radius-full)', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${data.total ? (count / data.total) * 100 : 0}%`,
                  background: 'var(--color-brand-primary)',
                }}
              />
            </div>
            <span style={{ width: 30, fontSize: 'var(--text-sm)', color: 'var(--color-text-tertiary)' }}>{count}</span>
          </div>
        ))}
      </Card>
    </div>
  );
}
